import { Box, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material"
import { AppMisc } from "../../../../misc/appMisc"
import { AppUtils } from "../../../../utils/appUtils"
import { useEffect, useRef, useState } from "react"
import AHMIS, { API_URL } from "../../../../api/api"
import AddAlertMessage from "../../../../components/alert/Alert"
import { AYURVEDIC_THERAPY, ERROR, JALCCOD_OPTIONS, MULDARTAA, SOMETHING_WENT_WRONG } from "../../../../utils/constants"
import { ICD_CODE_OPTIONS, MAJOR_DIAGNOSIS_FOR_INJURIES, MAJOR_DIAGNOSTICS_FOR_MENTAL_HEALTH, MAJOR_DIAGNOSTICS_FOR_NON_COMMUNICABLE_DISEASE } from "../../../../utils/constants/forms"
import { DateUtils } from "../../../../utils/dateUtils"
import styles from "./styles"
import ReactToPrint from "react-to-print"
import { CreditCard, Print } from "@mui/icons-material"
import HealthServiceCardHeader from "../health-service-card-header/HealthServiceCardHeader"

const HealthServiceCard = (props) => {
  const [healthServiceCardTableData, setHealthServiceCardTableData] = useState();
  const id = props.id || AppUtils.getUrlParam("id");
  const constantEnum = props.constantEnum || AppUtils.getUrlParam("constantEnum");
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const printComponentRef = useRef();
  const classes = styles();

  useEffect(() => {
    getTableData();
  }, []);

  const getTableData = async () => {
    try {
      const response = await AHMIS.get(API_URL.healthServiceCard, { params: { constantEnum, id } });
      const cardData = response?.data?.data || [];
      setHealthServiceCardTableData(
        Array.isArray(cardData) ? cardData : [cardData]
      );
    } catch (error) {
      AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
    }
  };

  const getVitalsFormat = (temperature, pulseRate, respRate, bloodPressure, spO2, bmi) => {
    return (
      (temperature || pulseRate || respRate || bloodPressure || spO2 || bmi) &&
      <Box>
        <Typography variant="subtitle2">Vitals:</Typography>
        <Box ml={1} mb={1}>
          {temperature ? <div>Temp : {temperature} &deg;C</div> : ""}
          {pulseRate ? <div>Pulse : {pulseRate} bpm</div> : ""}
          {respRate ? <div>R/R : {respRate} /min</div> : ""}
          {bloodPressure ? <div>BP: {bloodPressure} mmHg</div> : ""}
          {spO2 ? <div>SpO2 Level: {spO2} %</div> : ""}
        </Box>
      </Box>
    )
  }

  const hasMultipleHealthServiceCards =
    healthServiceCardTableData?.length > 1;

  const patientInfo = healthServiceCardTableData?.[0];

  const tableData = activeCardIndex === 0 ? healthServiceCardTableData?.slice(0, 1) : healthServiceCardTableData;

  const handleClick = (index) => {
    setActiveCardIndex(index);
  };

  return (
    <>
      {hasMultipleHealthServiceCards && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <>
            <Tooltip title="नवीनतम स्वास्थ्य विवरण सहितको स्वास्थ्य सेवा कार्ड हेर्न यहाँ क्लिक गर्नुहोस" placement="top" arrow>
              <IconButton className={activeCardIndex === 0 ? classes.icon : ""} onClick={() => handleClick(0)}>
                <CreditCard />
              </IconButton>
            </Tooltip>
            <Tooltip title="हालसम्मको स्वास्थ्य विवरण सहितको स्वास्थ्य सेवा कार्ड हेर्न यहाँ क्लिक गर्नुहोस" placement="top" arrow>
              <IconButton className={activeCardIndex === 1 ? classes.icon : ""} onClick={() => handleClick(1)}>
                <CreditCard />
              </IconButton>
            </Tooltip>
          </>
        </Box>
      )
      }
      <ReactToPrint
        trigger={() => (
          <Box display={"flex"} justifyContent={"flex-end"} margin={3}>
            <IconButton>
              <Print />
            </IconButton>
          </Box>
        )}
        content={() => printComponentRef.current}
      />
      {
        healthServiceCardTableData &&
        <Box ref={printComponentRef}>
          <HealthServiceCardHeader />
          <Typography align="end" my={1}>{`मिति : ${AppUtils.replaceWithNepaliDigit(DateUtils.getDateFromMilliseconds(patientInfo?.dartaaMiti))}`}</Typography>
          <TableContainer>
            <Table classes={{ root: "table-bordered normal-spacing" }} size="small" aria-label="Health Service Card">
              <TableHead classes={{ root: "align-center" }}>
                <TableRow>
                  <TableCell>मूल दर्ता नं</TableCell>
                  <TableCell>{patientInfo?.mulDartaaNumber}</TableCell>
                  <TableCell>नाम, थर</TableCell>
                  <TableCell>जाति कोड</TableCell>
                  <TableCell>लिङ्ग</TableCell>
                  <TableCell>उमेर</TableCell>
                  <TableCell>राष्ट्रिय परिचयपत्र नं.</TableCell>
                  <TableCell>शुल्क</TableCell>
                </TableRow>
              </TableHead>
              <TableBody classes={{ root: "align-center" }}>
                <TableRow>
                  <TableCell>ORC दर्ता नं.</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>{patientInfo.patientFirstName} {patientInfo.patientLastName}</TableCell>
                  <TableCell>{patientInfo?.casteCode && AppMisc.getCasteCodeLabelWithTooltip(patientInfo.casteCode)}</TableCell>
                  <TableCell>{AppMisc.getGenderLabel(patientInfo.gender)}</TableCell>
                  <TableCell>{patientInfo?.age && AppUtils.replaceWithNepaliDigit(patientInfo.age) + " "} {patientInfo?.ageUnit && AppMisc.getAgeUnitLabel(patientInfo.ageUnit)}</TableCell>
                  <TableCell>{patientInfo?.nid ? AppUtils.replaceWithNepaliDigit(patientInfo.nid) : "-"}</TableCell>
                  <TableCell>{patientInfo?.cost && patientInfo?.cost === "नि:शुल्क" ? patientInfo?.cost : AppUtils.replaceWithNepaliDigit(patientInfo?.cost)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Box mt={1.5} />
            <Table classes={{ root: "table-bordered normal-spacing" }} size="small" aria-label="Health Service Card">
              <TableHead classes={{ root: "align-center" }}>
                <TableRow>
                  <TableCell rowSpan={2}>ठेगाना</TableCell>
                  <TableCell>जिल्ला</TableCell>
                  <TableCell>नगर/गाउँपालिका</TableCell>
                  <TableCell>गाउँ/टोल</TableCell>
                  <TableCell>वडा नं.</TableCell>
                  <TableCell>सम्पर्क नम्बर</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{AppMisc.getDistrictName(patientInfo.district)}</TableCell>
                  <TableCell>{AppMisc.getMunicipalityName(patientInfo?.palikaName)}</TableCell>
                  <TableCell>{patientInfo?.gaunOrTole || "-"}</TableCell>
                  <TableCell>{AppUtils.replaceWithNepaliDigit(patientInfo?.wardNumber)}</TableCell>
                  <TableCell>{AppUtils.replaceWithNepaliDigit(patientInfo?.phoneNumber)}</TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
          <TableContainer className={classes.marginTop}>
            <Table classes={{ root: "table-bordered normal-spacing" }} size="small" aria-label="Health Service Card">
              <TableHead classes={{ root: "align-center" }}>
                <TableRow>
                  <TableCell>मिति</TableCell>
                  <TableCell>दर्ता नं.</TableCell>
                  {!props?.fromSeniorCitizen && <TableCell>Vitals with Examination</TableCell>}
                  <TableCell>अनुसन्धानमुलक परिक्षण</TableCell>
                  <TableCell>निदान</TableCell>
                  <TableCell>उपचार</TableCell>
                  <TableCell>सल्लाह</TableCell>
                  <TableCell>फर्केर आउने मिति</TableCell>
                </TableRow>
              </TableHead>
              <TableBody classes={{ root: "align-center" }}>
                {constantEnum !== MULDARTAA ? (
                  tableData?.map((healthServiceCardTableData, index) => (
                    <TableRow key={index}>

                      <TableCell>
                        {healthServiceCardTableData?.dartaaMiti
                          ? DateUtils.getDateFromMilliseconds(healthServiceCardTableData.dartaaMiti)
                          : ""}
                      </TableCell>

                      <TableCell>
                        {healthServiceCardTableData?.sewaDartaaNumber ||
                          healthServiceCardTableData?.mulDartaaNumber}
                      </TableCell>

                      {!props?.fromSeniorCitizen && (
                        <TableCell>
                          {getVitalsFormat(
                            healthServiceCardTableData?.temperature ||
                            healthServiceCardTableData?.temperatureInCelsius,
                            healthServiceCardTableData?.pulseRate,
                            healthServiceCardTableData?.respPerMin,
                            healthServiceCardTableData?.bloodPressure,
                            healthServiceCardTableData?.spO2,
                            healthServiceCardTableData?.bmi ||
                            healthServiceCardTableData?.bmiValue
                          )}

                          {healthServiceCardTableData?.height && (
                            <Box mb={1}>
                              Height: {healthServiceCardTableData.height} ft (
                              {AppUtils.feetToInch(healthServiceCardTableData.height)} inch)
                            </Box>
                          )}

                          {healthServiceCardTableData?.weight && (
                            <Box mb={1}>
                              Weight: {healthServiceCardTableData.weight + " k.g"}
                            </Box>
                          )}

                          {healthServiceCardTableData?.onExamination && (
                            <Box>{`OE: ${healthServiceCardTableData.onExamination}`}</Box>
                          )}
                        </TableCell>
                      )}

                      <TableCell>
                        {healthServiceCardTableData?.labTests || "-"}

                        {healthServiceCardTableData?.researchOrientedTestList?.length > 0 && (
                          <ol>
                            {healthServiceCardTableData.researchOrientedTestList.map((item) => (
                              <li key={item.labTestId}>{item.name}</li>
                            ))}
                          </ol>
                        )}
                      </TableCell>

                      <TableCell>
                        {healthServiceCardTableData?.provisionalDiagnosis ||
                          healthServiceCardTableData?.icdCode ||
                          healthServiceCardTableData?.diagnosis ||
                          healthServiceCardTableData?.majorClassifications ? (
                          <>
                            {healthServiceCardTableData.provisionalDiagnosis && (
                              <Box>{healthServiceCardTableData.provisionalDiagnosis}</Box>
                            )}

                            {healthServiceCardTableData.icdCode && (
                              <Box>
                                ICD:{" "}
                                {AppMisc.getIcdCodeObject(healthServiceCardTableData.icdCode)
                                  ?.label || healthServiceCardTableData.icdCode}
                              </Box>
                            )}

                            {healthServiceCardTableData.majorClassifications?.length > 0 && (
                              <Box>
                                {AppMisc.getMajorClassificationsLabel(
                                  healthServiceCardTableData.majorClassifications,
                                  props.majorClassificationsOptions
                                )}
                              </Box>
                            )}
                          </>
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      <TableCell>
                        {healthServiceCardTableData?.ayurvedicTherapy && (
                          <Box mb={1}>
                            {AppMisc.getLabelsFromArrayValue(
                              healthServiceCardTableData.ayurvedicTherapy,
                              AYURVEDIC_THERAPY
                            )}
                          </Box>
                        )}

                        {healthServiceCardTableData?.medicineDetails?.length > 0 && (
                          <Box mb={1}>
                            औषधि :{" "}
                            {healthServiceCardTableData.medicineDetails
                              .map((m) => `${m.medicineName} (${m.quantity})`)
                              .join(", ")}
                          </Box>
                        )}

                        {healthServiceCardTableData?.medicineDetailList && (
                          <Box mb={1}>
                            {AppMisc.getReadableMedicineDetailsFromList(
                              healthServiceCardTableData.medicineDetailList,
                              props.medicineInfo || []
                            )}
                          </Box>
                        )}
                      </TableCell>

                      <TableCell>
                        {healthServiceCardTableData?.tratementAndSuggestion || "-"}
                      </TableCell>

                      <TableCell>
                        {healthServiceCardTableData?.followUpDate
                          ? AppUtils.replaceWithNepaliDigit(
                            DateUtils.getDateFromMilliseconds(
                              healthServiceCardTableData?.followUpDate
                            )
                          )
                          : "-"}
                      </TableCell>

                    </TableRow>
                  ))
                ) : (
                  <TableRow className={classes.emptyHealthServiceCardHeight}>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography align="center" mt={2}>अर्को पटक आउँदा यो पुर्जा अनिवार्य लिएर आउनु होला।</Typography>
        </Box >
      }
    </>
  )
}

export default HealthServiceCard
